import React from 'react';

/**
 * Generate correct subscripted variants of coefficient strings
 *
 * @param  string             coefficient
 * @param  string             output_type [html|pdf|react] - the type of output to provide - default is 'react'
 * @return object|html        converted string
 */

export class Subscript {
  constructor(coefficient, output_type = 'react') {
    this.coefficient = coefficient.toLowerCase().trim();
    if (['html', 'pdf', 'react'].indexOf(output_type) === -1)
      throw new Error(
        `The output_type (provided: ${output_type}) argument to Subscript must be either 'react', 'html' or 'pdf'`
      );

    this.output_type = output_type;

    this.output_obj = {
      uc: false,
      lc: false,
    };

    this.morph();
  }

  /**
   * Dictionary of coefficients mapped to their rules for rendering them.
   * @return {Object} a mapping of the coefficient string name to an object consisting of the capitalized first part, and the text content of the subscript
   */

  static coefficient_map() {
    return {
      ssuh: {
        uc: 'SsUH',
        lc: '',
      },
      crs: {
        uc: 'CR',
        lc: 'S',
      },
      ssrt: {
        uc: 'SsRT',
        lc: '',
      },
      ssd: {
        uc: 'SsD',
        lc: '',
      },
      sxs: {
        uc: 'S',
        lc: 'XS',
      },
      s1uh: {
        uc: 'S1UH',
        lc: '',
      },
      cr1: {
        uc: 'CR',
        lc: '1',
      },
      s1rt: {
        uc: 'S1RT',
        lc: '',
      },
      s1d: {
        uc: 'S1D',
        lc: '',
      },
      sx1: {
        uc: 'S',
        lc: 'X1',
      },
      ss: {
        uc: 'S',
        lc: 'S',
      },
      s1: {
        uc: 'S',
        lc: '1',
      },
      fa: {
        uc: 'F',
        lc: 'a',
      },
      fv: {
        uc: 'F',
        lc: 'v',
      },
      sms: {
        uc: 'S',
        lc: 'MS',
      },
      sm1: {
        uc: 'S',
        lc: 'M1',
      },
      sds: {
        uc: 'S',
        lc: 'DS',
      },
      sd1: {
        uc: 'S',
        lc: 'D1',
      },
      tl: {
        uc: 'T',
        lc: 'L',
      },
      pga: {
        uc: 'PGA',
        lc: '',
      },
      pgam: {
        uc: 'PGA',
        lc: 'M',
      },
      fpga: {
        uc: 'F',
        lc: 'PGA',
      },
      ie: {
        uc: 'I',
        lc: 'e',
      },
      // an alias
      le: {
        uc: 'I',
        lc: 'e',
      },
      cv: {
        uc: 'C',
        lc: 'v',
      },
      ts: {
        uc: 'T',
        lc: '(s)',
      },
      sag: {
        uc: 'S',
        lc: 'a(g)',
      },
      sdc: {
        uc: 'S',
        lc: 'DC',
      },
      vs30: {
        uc: 'V',
        lc: 'S30',
      },
      // The 2 values below are for Tornado, so do not remove them - putting them here saves us duplicating logic
      ae: {
        uc: 'A',
        lc: 'e',
      },
      vt: {
        uc: 'V',
        lc: 't',
      },
    };
  }

  // This can be used to calculate the width of the string more accurately. Was computed with:
  /*
	window.chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ".split('');
	window.span = document.createElement('span');
	document.body.appendChild(span);
	window.widthMap = {};
	window.widths = [];
	window.sum = 0;
	chars.forEach(function(letter){
		window.span.innerHTML = letter;
		window.widthMap[letter] = window.span.offsetWidth;
		window.widths.push(window.span.offsetWidth)
		window.sum += window.span.offsetWidth;
		console.log(letter, window.span.offsetWidth);
	});
	console.log('average is: ' + window.sum/chars.length);

	Average is 7
	*/
  static get character_widths() {
    return {
      a: 6,
      b: 7,
      c: 6,
      d: 7,
      e: 7,
      f: 4,
      g: 7,
      h: 7,
      i: 3,
      j: 3,
      k: 6,
      l: 3,
      m: 10,
      n: 7,
      o: 7,
      p: 7,
      q: 7,
      r: 4,
      s: 5,
      t: 4,
      u: 7,
      v: 6,
      w: 9,
      x: 6,
      y: 6,
      z: 5,
      A: 8,
      B: 8,
      C: 8,
      D: 9,
      E: 7,
      F: 7,
      G: 9,
      H: 9,
      I: 3,
      J: 6,
      K: 8,
      L: 6,
      M: 11,
      N: 9,
      O: 10,
      P: 7,
      Q: 10,
      R: 7,
      S: 7,
      T: 7,
      U: 8,
      V: 7,
      W: 11,
      X: 8,
      Y: 7,
      Z: 7,
    };
  }

  /**
   * A getter to allow getting the coefficient map via easily remembered property
   * @return {Object} Subscript.coefficient_map()
   */

  static get coefficients() {
    return Subscript.coefficient_map();
  }

  /**
   * Get the output specs from coefficient_map(); sets the output_obj property on the instance
   * @return {Object} this.output_obj
   */

  morph() {
    this.output_obj = Subscript.coefficient_map()[this.coefficient];
    if (typeof this.output_obj === 'undefined')
      throw new Error(
        `The coefficient (${this.coefficient}) provided is not valid. It has not been specified in the method: coefficient_map in Subscript.js`
      );
    return this.output_obj;
  }

  output_html() {
    let subscript = this.output_obj.lc.length ? `<sub>${this.output_obj.lc}</sub>` : '';
    return `<span>${this.output_obj.uc}${subscript}</span>`;
  }

  output_react() {
    return <SubscriptComponent output_obj={this.output_obj} />;
  }

  output_pdf() {}

  /**
   * Return the class in it's specified rendered form, specific to the output type specified on construction.
   * This function calls one of the private methods: [`output_html`, `output_react`, `output_pdf`] based on the string content of the property `this.output_type`
   * @return {Object} sum
   */

  output() {
    if (this.output_obj.uc === false || this.output_obj.lc === false)
      throw new Error(
        `An error occurred when generating a subscript, please check the value (${this.coefficient}) that has been provided to Subscript`
      );

    return this[`output_${this.output_type}`]();
  }

  toString() {
    return this.output();
  }
}

/**
 * Component that is returned from Subscript when 'react' is specified as the output type
 * @return React.Component
 */

export class SubscriptComponent extends React.Component {
  render() {
    if (this.props.output_obj.lc.length) {
      return (
        <span>
          {this.props.output_obj.uc}
          <sub>{this.props.output_obj.lc}</sub>
        </span>
      );
    } else {
      return <span>{this.props.output_obj.uc}</span>;
    }
  }
}

// For testing purposes
export class SubscriptTest {
  constructor() {
    for (let i in Subscript.coefficients) {
      console.log(i, new Subscript(i) + '');
    }
    console.log('sdfsdEE', new Subscript('sdfsdEE') + '');
  }
}
